import { clearFilters } from '../mutators/capabilityFilterMutators';
import getPreferredRoomLists from '../services/getPreferredRoomLists';
import getRoomListsFromPlacesApi from '../services/getRoomListsFromPlacesApi';
import getRoomListsService from '../services/getRoomListsService';
import getStore from '../store/store';
import getCities from '../utils/getCities';
import { getRoomsFromPlacesApi, updateRoomsFromPlacesApi } from '../utils/getRooms';
import type PlacesFilters from 'owa-calendar-scheduling-service/lib/schema/PlacesFilters';
import { isFeatureEnabled } from 'owa-feature-flags';
import loc from 'owa-localize';
import { anyRoomListName } from 'owa-locstrings/lib/strings/anyroomlistname.locstring.json';
import { orchestrator } from 'satcheljs';
import {
    initializeRoomList,
    getRoomsForList,
    updateRoomListsInStore,
    initializeRoomListPicker,
    fetchRoomsAvailabilityForRoomPanel,
} from '../actions/calendarRoomActions';
import { PerformanceDatapoint } from 'owa-analytics';
import { updateShowPlaceType } from '../mutators/calendarRoomMutators';

export default orchestrator(initializeRoomList, async actionMessage => {
    const {
        isConsumer,
        eventStartDateTime,
        eventEndDateTime,
        isAllDayEvent,
        recurrence,
        requiredAttendees,
        optionalAttendees,
        originalEvent,
    } = actionMessage;

    // Initialize Rooms
    const viewState = getStore();
    let hasCurrentRoomList: boolean = false;
    const performanceDatapoint = new PerformanceDatapoint('RoomPanelInitialLoad');
    if (
        viewState == null ||
        isConsumer /* If its a consumer user, then we don't want to initialize the roomList*/
    ) {
        return;
    }

    // Clear any existing filters from previous sessions
    clearFilters();

    if (viewState.roomListViewState?.currentRoomList?.email) {
        hasCurrentRoomList = true;
    }

    // Get all rooms in the user's default room list
    if (isFeatureEnabled('cal-advancedRoomBooking')) {
        let roomFilters: PlacesFilters = {};

        if (hasCurrentRoomList) {
            // Set the last selected room list as the default one
            roomFilters.roomListEmails = [viewState.roomListViewState.currentRoomList.email];

            if (isFeatureEnabled('cal-mf-advancedRoomBookingV2')) {
                updateRoomListsInStore([viewState.roomListViewState.currentRoomList]);
            }
        }

        // Check to see if current tenant has any workspaces. If they do then update showPlaceType in order
        // to render the PlaceType filter
        if (isFeatureEnabled('cal-mf-usePlacesVNext') && isFeatureEnabled('cal-mf-spaces')) {
            getRoomsFromPlacesApi({
                shouldGetSpaces: true,
            }).then(workspaces => {
                if (workspaces?.length > 0) {
                    updateShowPlaceType(true);
                }
            });
        }

        await updateRoomsFromPlacesApi(roomFilters);

        // fetch availability for rooms
        fetchRoomsAvailabilityForRoomPanel(
            eventStartDateTime,
            eventEndDateTime,
            isAllDayEvent,
            recurrence,
            requiredAttendees,
            optionalAttendees,
            originalEvent
        );
    } else {
        let roomList: string = hasCurrentRoomList
            ? viewState.roomListViewState.currentRoomList.email
            : null;
        getRoomsForList(roomList);
    }

    const roomLists = isFeatureEnabled('cal-advancedRoomBooking')
        ? await getRoomListsFromPlacesApi()
        : await getRoomListsService().then(response => [
              { name: loc(anyRoomListName), email: loc(anyRoomListName) },
              ...response?.Body?.RoomLists?.map(result => ({
                  name: result.Name,
                  email: result.EmailAddress,
              })),
          ]);

    updateRoomListsInStore(roomLists);

    if (isFeatureEnabled('cal-mf-advancedRoomBookingV2')) {
        Promise.all([getPreferredRoomLists(), getCities()]).then(([preferredRoomLists, cities]) => {
            if (!preferredRoomLists || preferredRoomLists.length === 0) {
                preferredRoomLists = [viewState.roomListViewState.defaultRoomList];
            }
            let citiesAndRoomLists = {};
            if (cities) {
                citiesAndRoomLists = cities.reduce((cityRoomLists, city) => {
                    cityRoomLists[city] = [];
                    return cityRoomLists;
                }, {});
            }
            initializeRoomListPicker(preferredRoomLists, citiesAndRoomLists);
        });
    }
    performanceDatapoint.end();
});
